import { connect, type ConnectedProps } from "react-redux";

import { DetailsHeader } from "components";
import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { type OutstandingDetailsHeaderOwnProps } from "components/sectionHeader/detailsHeader/types";
import SelectedItemsLabel from "components/sectionHeader/selectedItemsLabel/SelectedItemsLabel";
import { RolePermissions } from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import Restricted from "features/Application/Restricted";
import { AddPacksButton } from "features/Licensing/Packs/AssociatedPacks/AddPacksButton";
import { bindAction } from "interfaces";
import { noop } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { getPeopleEntityLabel } from "utils/peopleAssignmentModalUtils";
import { lockItemAction } from "../state/thunks/EntityStateThunk";

interface EditHeaderContainerPropsBase extends OutstandingDetailsHeaderOwnProps {
  scormId: number;
  overviewUrl: string;
  canAddPacks: boolean;
  showAddPacksButton: boolean;
  back: () => void;
}

export type EditDetailsHeaderProps = EditHeaderContainerPropsBase & PropsFromRedux;

export const EditDetailsHeader = (props: EditDetailsHeaderProps) => {
  const {
    lockItem,
    selectedUserIds,
    selectedGroupIds,
    back,
    canAddPacks,
    scormId,
    title,
    overviewUrl,
    showAddPacksButton,
  } = props;

  const [packsModalShown, setPacksModalShown] = useState(false);
  const renderAddPackButton = (hasAnyPermission: boolean) => (
    <AddPacksButton disabled={!canAddPacks} hasPermission={hasAnyPermission} onClick={() => setPacksModalShown(true)} />
  );

  const navigate = useNavigate();
  const onClose = useCallback(() => navigate(overviewUrl), [navigate, overviewUrl]);
  const onEdit = useCallback(() => lockItem(scormId), [lockItem, scormId]);

  const headerTitle = useMemo(() => {
    const selectedPeopleCount = selectedUserIds.length + selectedGroupIds.length;

    if (selectedPeopleCount) {
      return (
        <SelectedItemsLabel
          itemName={getPeopleEntityLabel(selectedUserIds.length, selectedGroupIds.length)}
          selectedItemsAmount={selectedPeopleCount}
          shouldPluralize={false}
        />
      );
    } else {
      return title;
    }
  }, [title, selectedGroupIds.length, selectedUserIds.length]);

  const selectedItemIds = useMemo(() => [scormId], [scormId]);
  return (
    <DetailsHeader
      title={headerTitle}
      pageTitle={props.title}
      titleForGA="Asset Details"
      backButton={back || overviewUrl}
      defaultURL={overviewUrl}
      canBeEdited={props.canBeEdited}
      crudPermission={props.crudPermission}
      canBePublished={props.canBePublished}
      isRevertVisible={props.isRevertVisible}
      invalidFormDetails={props.invalidFormDetails}
      publishedStatus={props.publishedStatus}
      entityStateActions={{
        onClose,
        onEdit,
        onPublish: props.entityStateActions.onPublish,
        onRevert: props.entityStateActions.onRevert,
      }}
      hideEntityStateButtons={props.hideEntityStateButtons}
    >
      {showAddPacksButton && (
        <Restricted
          permissions={[RolePermissions.AssetsCreate, RolePermissions.PacksManage]}
          renderContent={(hasAnyPermission) => renderAddPackButton(hasAnyPermission)}
        />
      )}
      <AddAssetsToPackModal
        showModal={packsModalShown}
        onClose={() => setPacksModalShown(false)}
        selectedItemIds={selectedItemIds}
        contentType="Scorm"
        onComplete={noop}
      />
    </DetailsHeader>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const editItem = state.library.scorm.edit;
  return {
    isPeopleSelected: !!(editItem.users.selectedIds.length || editItem.groups.selectedIds.length),
    selectedUserIds: editItem.users.selectedIds,
    selectedGroupIds: editItem.groups.selectedIds,
    selectedUsers: editItem.users.selectedItems,
    selectedGroups: editItem.groups.selectedItems,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  lockItem: bindAction(lockItemAction, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(EditDetailsHeader);
export default ConnectedComponent;
